import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faServer, 
  faDatabase, 
  faCloud, 
  faCode, 
  faLayerGroup,
  faSpinner,
  faSearch,
  faTimes as faClearSearch,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';

export interface ResourceDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  resourceType: 'ec2' | 'rds' | 's3' | 'lambda' | 'eks';
  accountId?: string;
  onFetchDetails: (resourceType: string, accountId?: string) => Promise<any[]>;
}

const resourceConfig = {
  ec2: {
    title: 'EC2 Instances',
    icon: faServer,
    color: 'blue',
    columns: ['Name', 'Instance ID', 'Type', 'Status', 'Region', 'Availability Zone', 'Public IP', 'Private IP', 'Launch Time', 'Key Pair', 'Security Groups', 'VPC ID']
  },
  rds: {
    title: 'RDS Instances',
    icon: faDatabase,
    color: 'green',
    columns: ['Name', 'DB Instance ID', 'Engine', 'Version', 'Status', 'Region', 'Availability Zone', 'Endpoint', 'Port', 'Storage', 'Instance Class', 'Multi-AZ']
  },
  s3: {
    title: 'S3 Buckets',
    icon: faCloud,
    color: 'orange',
    columns: ['Name', 'Region', 'Creation Date', 'Versioning', 'Encryption', 'Public Access', 'Storage Class']
  },
  lambda: {
    title: 'Lambda Functions',
    icon: faCode,
    color: 'yellow',
    columns: ['Name', 'Runtime', 'Status', 'Memory', 'Timeout', 'Handler', 'Code Size', 'Last Modified', 'Version', 'Environment']
  },
  eks: {
    title: 'EKS Clusters',
    icon: faLayerGroup,
    color: 'purple',
    columns: ['Name', 'Version', 'Status', 'Region', 'Endpoint', 'Platform Version', 'Node Groups', 'Fargate Profiles', 'Created At', 'Role ARN']
  }
};

export const ResourceDetailsModal: React.FC<ResourceDetailsModalProps> = ({
  isOpen,
  onClose,
  resourceType,
  accountId,
  onFetchDetails
}) => {
  const [resources, setResources] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterRegion, setFilterRegion] = useState<string>('all');
  const [filterAccount, setFilterAccount] = useState<string>('all');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const config = resourceConfig[resourceType];

  // Get unique values for filter dropdowns
  const filterOptions = useMemo(() => {
    const types = new Set<string>();
    const statuses = new Set<string>();
    const regions = new Set<string>();
    const accounts = new Set<string>();

    resources.forEach(resource => {
      if (resource.metadata?.instanceType) types.add(resource.metadata.instanceType);
      if (resource.metadata?.engine) types.add(resource.metadata.engine);
      if (resource.metadata?.runtime) types.add(resource.metadata.runtime);
      if (resource.status) statuses.add(resource.status);
      if (resource.region) regions.add(resource.region);
      if (resource.accountName) accounts.add(resource.accountName);
    });

    return {
      types: Array.from(types).sort(),
      statuses: Array.from(statuses).sort(),
      regions: Array.from(regions).sort(),
      accounts: Array.from(accounts).sort()
    };
  }, [resources]);

  // Filter resources based on search term + dropdown filters
  const filteredResources = useMemo(() => {
    let filtered = resources;

    // Apply dropdown filters
    if (filterAccount !== 'all') {
      filtered = filtered.filter(r => r.accountName === filterAccount);
    }
    if (filterType !== 'all') {
      filtered = filtered.filter(r =>
        r.metadata?.instanceType === filterType ||
        r.metadata?.engine === filterType ||
        r.metadata?.runtime === filterType
      );
    }
    if (filterStatus !== 'all') {
      filtered = filtered.filter(r => r.status === filterStatus);
    }
    if (filterRegion !== 'all') {
      filtered = filtered.filter(r => r.region === filterRegion);
    }

    // Apply search
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(resource => {
        const getValue = (path: string) => {
          return path.split('.').reduce((obj: any, key: string) => obj?.[key], resource) || '';
        };

        const basicSearch = [
          resource.name,
          resource.id,
          resource.status,
          resource.region,
          getValue('metadata.instanceType'),
          getValue('metadata.publicIpAddress'),
          getValue('metadata.privateIpAddress'),
          getValue('metadata.engine'),
          getValue('metadata.runtime')
        ].some(value =>
          value && String(value).toLowerCase().includes(searchLower)
        );

        const tagSearch = resource.tags && Object.entries(resource.tags).some(([key, value]) =>
          key.toLowerCase().includes(searchLower) ||
          String(value).toLowerCase().includes(searchLower)
        );

        return basicSearch || tagSearch;
      });
    }

    return filtered;
  }, [resources, searchTerm, filterAccount, filterType, filterStatus, filterRegion]);

  const fetchResourceDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const details = await onFetchDetails(resourceType, accountId);
      setResources(details);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch resource details');
    } finally {
      setLoading(false);
    }
  }, [resourceType, accountId, onFetchDetails]);

  useEffect(() => {
    if (isOpen) {
      fetchResourceDetails();
    }
  }, [isOpen, fetchResourceDetails]);


  const renderResourceValue = (resource: any, column: string) => {
    const getValue = (path: string) => {
      return path.split('.').reduce((obj, key) => obj?.[key], resource) || 'N/A';
    };

    switch (column) {
      case 'Name':
        return resource.name || resource.id || getValue('metadata.name') || 'N/A';
      case 'Instance ID':
      case 'DB Instance ID':
        return resource.id;
      case 'Type':
        return getValue('metadata.instanceType') || getValue('metadata.dbInstanceClass');
      case 'Status':
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            resource.status === 'running' || resource.status === 'available' || resource.status === 'active' || resource.status === 'ACTIVE'
              ? 'bg-green-100 text-green-800'
              : resource.status === 'stopped' || resource.status === 'FAILED'
              ? 'bg-red-100 text-red-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}>
            {resource.status}
          </span>
        );
      case 'Region':
        return resource.region;
      case 'Availability Zone':
        return resource.metadata?.availabilityZone || resource.metadata?.placement?.availabilityZone || 'N/A';
      case 'Public IP':
        return resource.metadata?.publicIp || resource.metadata?.publicIpAddress || 'N/A';
      case 'Private IP':
        return resource.metadata?.privateIp || resource.metadata?.privateIpAddress || 'N/A';
      case 'Launch Time':
        const launchTime = resource.metadata?.launchTime;
        return launchTime ? new Date(launchTime).toLocaleString() : 'N/A';
      case 'Key Pair':
        return resource.metadata?.keyName || 'N/A';
      case 'Security Groups':
        const securityGroups = resource.metadata?.securityGroups;
        return Array.isArray(securityGroups) ? securityGroups.map((sg: any) => sg.groupName || sg.groupId).join(', ') : 'N/A';
      case 'VPC ID':
        return getValue('metadata.vpcId');
      case 'Engine':
        return getValue('metadata.engine');
      case 'Version':
        return resource.metadata?.engineVersion || resource.metadata?.version || 'N/A';
      case 'Endpoint':
        const endpoint = getValue('metadata.endpoint');
        return typeof endpoint === 'object' ? endpoint.address : endpoint;
      case 'Port':
        const endpointObj = getValue('metadata.endpoint');
        return typeof endpointObj === 'object' ? endpointObj.port : getValue('metadata.port');
      case 'Storage':
        const storage = getValue('metadata.allocatedStorage');
        return storage !== 'N/A' ? `${storage} GB` : 'N/A';
      case 'Instance Class':
        return resource.metadata?.dbInstanceClass || resource.metadata?.instanceClass || 'N/A';
      case 'Multi-AZ':
        return getValue('metadata.multiAZ') ? 'Yes' : 'No';
      case 'Creation Date':
        const creationDate = resource.metadata?.creationDate || resource.metadata?.creationTime;
        return creationDate ? new Date(creationDate).toLocaleString() : 'N/A';
      case 'Versioning':
        return resource.metadata?.versioning ? 'Enabled' : 'Suspended';
      case 'Encryption':
        return resource.metadata?.encryption?.serverSideEncryption ? 'Enabled' : 'Disabled';
      case 'Public Access':
        return resource.metadata?.publicAccessBlock?.blockPublicAcls ? 'Blocked' : 'Allowed';
      case 'Storage Class':
        return resource.metadata?.storageClass || 'Standard';
      case 'Objects Count':
        return resource.metadata?.numberOfObjects || '0';
      case 'Runtime':
        return getValue('metadata.runtime');
      case 'Memory':
        const memory = getValue('metadata.memorySize');
        return memory !== 'N/A' ? `${memory} MB` : 'N/A';
      case 'Timeout':
        const timeout = getValue('metadata.timeout');
        return timeout !== 'N/A' ? `${timeout}s` : 'N/A';
      case 'Handler':
        return getValue('metadata.handler');
      case 'Code Size':
        const codeSize = getValue('metadata.codeSize');
        return codeSize !== 'N/A' ? `${(codeSize / 1024 / 1024).toFixed(2)} MB` : 'N/A';
      case 'Last Modified':
        const lastModified = getValue('metadata.lastModified');
        return lastModified !== 'N/A' ? new Date(lastModified).toLocaleString() : 'N/A';
      case 'Environment':
        const env = getValue('metadata.environment.variables');
        return env && typeof env === 'object' ? `${Object.keys(env).length} vars` : 'None';
      case 'Platform Version':
        return getValue('metadata.platformVersion');
      case 'Node Groups':
        const nodeGroups = getValue('metadata.nodeGroups');
        return Array.isArray(nodeGroups) ? nodeGroups.length.toString() : '0';
      case 'Fargate Profiles':
        const fargateProfiles = getValue('metadata.fargateProfiles');
        return Array.isArray(fargateProfiles) ? fargateProfiles.length.toString() : '0';
      case 'Created At':
        const createdAt = getValue('metadata.createdAt');
        return createdAt !== 'N/A' ? new Date(createdAt).toLocaleString() : 'N/A';
      case 'Role ARN':
        return getValue('metadata.roleArn');
      default:
        return 'N/A';
    }
  };

  const toggleExpanded = (id: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const renderTags = (tags: Record<string, string> = {}, resourceId: string) => {
    
    const tagEntries = Object.entries(tags);
    if (tagEntries.length === 0) return 'No tags';
    
    const isExpanded = expandedRows.has(resourceId);
    const visibleTags = isExpanded ? tagEntries : tagEntries.slice(0, 2);
    const hasMoreTags = tagEntries.length > 2;
    
    return (
      <div className="space-y-1">
        <div className="flex flex-wrap gap-1">
          {visibleTags.map(([key, value]) => (
            <span key={key} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs whitespace-nowrap">
              {key}: {value}
            </span>
          ))}
        </div>
        {hasMoreTags && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded(resourceId);
            }}
            className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 mt-1"
          >
            <FontAwesomeIcon 
              icon={isExpanded ? faChevronUp : faChevronDown} 
              className="w-3 h-3" 
            />
            {isExpanded ? 'Show less' : `+${tagEntries.length - 2} more tags`}
          </button>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-hidden">
      <div className="bg-white rounded-xl shadow-xl max-w-7xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FontAwesomeIcon 
                icon={config.icon} 
                className={`text-${config.color}-600 text-xl`} 
              />
              <h2 className="text-xl font-semibold text-gray-900">
                {config.title}
              </h2>
              {resources.length > 0 && (
                <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                  {filteredResources.length} of {resources.length} items
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xl" />
            </button>
          </div>
          
          {/* Search Box */}
          {resources.length > 0 && (
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FontAwesomeIcon icon={faSearch} className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={`Search ${config.title.toLowerCase()}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
              {searchTerm && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    onClick={() => setSearchTerm('')}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <FontAwesomeIcon icon={faClearSearch} className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Filter Dropdowns */}
          {resources.length > 0 && (
            <div className="flex items-center gap-3 flex-wrap">
              {filterOptions.accounts.length > 1 && (
                <select
                  value={filterAccount}
                  onChange={(e) => setFilterAccount(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Accounts</option>
                  {filterOptions.accounts.map(a => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              )}
              {filterOptions.types.length > 0 && (
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Types</option>
                  {filterOptions.types.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              )}
              {filterOptions.statuses.length > 0 && (
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Status</option>
                  {filterOptions.statuses.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              )}
              {filterOptions.regions.length > 0 && (
                <select
                  value={filterRegion}
                  onChange={(e) => setFilterRegion(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Regions</option>
                  {filterOptions.regions.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              )}
              {(filterAccount !== 'all' || filterType !== 'all' || filterStatus !== 'all' || filterRegion !== 'all') && (
                <button
                  onClick={() => { setFilterAccount('all'); setFilterType('all'); setFilterStatus('all'); setFilterRegion('all'); }}
                  className="px-3 py-1.5 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                >
                  Clear Filters
                </button>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <FontAwesomeIcon icon={faSpinner} className="text-2xl text-blue-600 animate-spin mb-2" />
                <p className="text-gray-600">Loading {config.title.toLowerCase()}...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <p className="text-red-600 mb-2">Error: {error}</p>
                <button
                  onClick={fetchResourceDetails}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : resources.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <p className="text-gray-500">No {config.title.toLowerCase()} found</p>
            </div>
          ) : filteredResources.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <FontAwesomeIcon icon={faSearch} className="text-4xl text-gray-300 mb-3" />
                <p className="text-gray-500 text-lg mb-1">No results found</p>
                <p className="text-gray-400 text-sm">Try adjusting your search term</p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="mt-3 px-4 py-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  Clear search
                </button>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-auto">
              <div className="p-6">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        {config.columns.map((column) => (
                          <th
                            key={column}
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap w-[150px]"
                          >
                            {column}
                          </th>
                        ))}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap w-[200px]">
                          Tags
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredResources.map((resource, index) => (
                        <tr key={resource.id || index} className="hover:bg-gray-50">
                          {config.columns.map((column) => (
                            <td key={column} className="px-4 py-4 text-sm text-gray-900 w-[150px] truncate" title={String(renderResourceValue(resource, column))}>
                              {renderResourceValue(resource, column)}
                            </td>
                          ))}
                          <td className="px-4 py-4 text-sm text-gray-900 w-[200px]">
                            {renderTags(resource.tags, resource.id || index.toString())}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-500">
            {resources.length > 0 && (
              searchTerm ? 
                `Showing ${filteredResources.length} of ${resources.length} ${config.title.toLowerCase()}` :
                `Showing ${resources.length} ${config.title.toLowerCase()}`
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={fetchResourceDetails}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Refresh
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};